import { Link } from "react-router-dom";
import { UserCheck, AlertTriangle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FamilyTimeline } from "@/components/FamilyTimeline";

const LABELS = { contact: "Mobile", email: "Email", childName: "Child name", parentName: "Parent name" };
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const THEMES = {
  weak: {
    box: "border-red-300 border-l-[6px] border-l-red-600 bg-red-50 shadow-[0_6px_24px_-10px_rgba(220,38,38,0.55)]",
    iconWrap: "bg-red-600 text-white", title: "text-red-800", text: "text-red-700/90",
    row: "border border-red-100 bg-white", chip: "bg-red-100 text-red-800", link: "text-red-700",
  },
  strong: {
    box: "border-gold-400 border-l-[6px] border-l-gold-600 bg-gold-50 shadow-[0_6px_24px_-12px_rgba(185,131,44,0.6)]",
    iconWrap: "bg-gold-600 text-white", title: "text-plum-950", text: "text-slate-700",
    row: "border border-gold-200 bg-white", chip: "bg-plum-100 text-plum-800", link: "text-plum-800",
  },
};

export const ReturningBanner = ({ matches, strongEarlier, isRevisit, onRevisit, query }) => {
  if (!matches?.length) return null;
  const t = THEMES[strongEarlier ? "strong" : "weak"];
  const Icon = strongEarlier ? UserCheck : AlertTriangle;
  return (
    <div role="alert" className={`no-print fade-up mb-6 rounded-xl border p-4 sm:p-5 ${t.box}`} data-testid="returning-banner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="relative mt-0.5 flex h-10 w-10 shrink-0">
            {!strongEarlier && <span className="absolute inset-0 animate-ping rounded-full bg-red-500/40" />}
            <span className={`relative grid h-10 w-10 place-items-center rounded-full ${t.iconWrap}`}><Icon className="h-5 w-5" /></span>
          </span>
          <div>
            <div className={`font-display text-base font-bold sm:text-lg ${t.title}`} data-testid="returning-banner-title">
              {strongEarlier ? "Returning parent — earlier visit found" : "Parent has visited earlier — please verify"}
            </div>
            <p className={`mt-0.5 text-sm ${t.text}`}>
              {strongEarlier
                ? "Matched by mobile / email or both names. Category is set to Revisit; Spot applies only on the first visit."
                : "Only a name matched. If this is the same family, mark it as Revisit."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <FamilyTimeline query={query} />
          {isRevisit ? (
            <Button size="sm" disabled className="bg-emerald-600 font-semibold text-white disabled:opacity-100" data-testid="returning-marked-revisit-btn"><Check className="mr-1 h-4 w-4" />Marked as Revisit</Button>
          ) : (
            <Button size="sm" onClick={onRevisit} className={`font-semibold text-white ${strongEarlier ? "bg-plum-700 hover:bg-plum-800" : "bg-red-600 hover:bg-red-700"}`} data-testid="returning-mark-revisit-btn">Mark as Revisit</Button>
          )}
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        {matches.slice(0, 3).map((m) => (
          <div key={m.id} className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-3 py-2 text-xs ${t.row}`} data-testid={`returning-match-${m.receipt_no}`}>
            <Link to={`/receipts/${m.id}`} className={`font-num font-bold underline-offset-2 hover:underline ${t.link}`}>{m.receipt_no}</Link>
            <span className="font-semibold text-plum-950">{fmt(m.visit_date)}</span>
            <span className="text-slate-700">{m.child || "—"} · {m.parent || "—"} · {m.grade}</span>
            <span className="text-slate-500">by {m.counsellor || "—"}</span>
            <span className="capitalize text-slate-500">{m.status.replace("_", " ")}</span>
            <span className="ml-auto flex gap-1">
              {m.matched_on.map((k) => <span key={k} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${t.chip}`}>{LABELS[k]}</span>)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
